import { useRef, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import DraggableModal from 'components/organisms/draggableModal';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import BaseForm from 'components/atoms/base-form';
import FormItemWrapper from 'components/molecules/rpm-alert-assessment/formItemWrapper';

import { assessmentWeightForm } from '../templates/weight';
import s from '../styles.module.scss';
import { criticalWeightValidationSchema } from '../validation-schema';
import { useWeightAssessment } from '../hooks/use-weight-assessment';
import { useStoreModalValue } from '../hooks/use-store-modal-value';
import { scrollToErrorElement } from '../helpers';
import { nursingInterventionOptions } from '../templates/common';

import type { ICriticalWeightProps } from '../types';
import type { FC } from 'react';

const CriticalWeightModal: FC<ICriticalWeightProps> = ({
  isOpen,
  activityId,
  type,
  idx,
  activityDate,
  patientGuid,
  values,
  handleSubmit,
  closeModal,
}) => {
  const {
    doctor: { addComment },
  } = useDispatch();

  const { t } = useTranslation();

  const { initialValues, onSaveForm } = useWeightAssessment({
    idx,
    activityDate,
    patientGuid,
    values,
    type,
    activityId,
    addComment,
    closeModal,
    handleSubmit,
  });
  type TInitialValues = typeof initialValues;
  const { parsedData, setModalValuesToStorage } =
    useStoreModalValue<TInitialValues>({
      idx,
      type,
      modalName: 'weight',
    });
  const blockRef = useRef<HTMLDivElement | null>(null);
  const formWrapperRef = useRef<HTMLDivElement>(null);

  const formik = useFormik({
    initialValues: parsedData ?? initialValues,
    onSubmit: (data) => {
      if (data.isOneMoreReading === 'false') {
        data.bounds = null;
        data.repeatReadingWas = null;
      }
      if (data.patient !== null) {
        data.other = null;
        data.relationship = null;
      }
      const isEscalation = data.nursingIntervention?.includes(
        nursingInterventionOptions[1].label,
      );
      if (!isEscalation) {
        data.escalationToClinic = [];
      }

      setModalValuesToStorage(data);

      onSaveForm(data);
    },
    validateOnChange: false,
    validationSchema: criticalWeightValidationSchema(),
  });

  const onSave = async () => {
    await formik.submitForm();
    scrollToErrorElement(formWrapperRef.current);
  };

  const isAgreeWeightDiscussValue = formik.values.isAgreeWeightDiscuss;
  const isAgreeWeightDiscussInitValue =
    formik.initialValues?.isAgreeWeightDiscuss;

  useEffect(() => {
    if (
      isAgreeWeightDiscussValue === false &&
      isAgreeWeightDiscussInitValue !== false
    ) {
      if (blockRef.current) {
        blockRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }
  }, [isAgreeWeightDiscussValue, isAgreeWeightDiscussInitValue]);

  const expandableExtraFields = ['isOneMoreReading'];

  return (
    <DraggableModal
      isOpen={isOpen}
      toggle={closeModal}
      title={t('labels.rpmAlertBasedAssessment', {
        name: t('labels.weight'),
      })}
      onSave={onSave}
    >
      <BaseForm formik={formik}>
        <div className={s.wrapper} ref={formWrapperRef}>
          {assessmentWeightForm.map((formItem, index) => (
            <FormItemWrapper
              formik={formik}
              index={index}
              formItem={formItem}
              type={type}
              ref={blockRef}
              key={formItem.key}
              isShowExtraFields={
                expandableExtraFields.includes(formItem.name)
                  ? formik.getFieldMeta(formItem.name)?.value === 'true'
                  : undefined
              }
            />
          ))}
        </div>
      </BaseForm>
    </DraggableModal>
  );
};

export default CriticalWeightModal;
